import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { categoryConfigs } from "@/mocks/categories";
import { specials } from "@/mocks/specials";
import { searchContent } from "@/utils/movieData";

const navLinkBase =
  "px-3 py-2 text-[15px] rounded-md transition-colors hover:bg-primary-100 hover:text-primary-800 whitespace-nowrap";

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [keyword, setKeyword] = useState("");
  const [showSuggest, setShowSuggest] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
    setShowSuggest(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggest(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const suggestions = keyword.trim() ? searchContent(keyword) : { movies: [], news: [] };

  const submitSearch = () => {
    const q = keyword.trim();
    if (!q) return;
    setShowSuggest(false);
    setMobileOpen(false);
    navigate(`/category/popular?q=${encodeURIComponent(q)}`);
  };

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* top strip */}
      <div className="hidden md:block bg-primary-950 text-background-200">
        <div className="mx-auto flex h-9 max-w-[1280px] items-center justify-between px-4 md:px-6 text-xs">
          <p className="flex items-center gap-2">
            <i className="ri-movie-2-line text-accent-400"></i>
            秋霞电影 · 发现每一部好电影
          </p>
          <div className="flex items-center gap-5">
            <a href="mailto:contact@qiuxiamovie.com" className="flex items-center gap-1.5 hover:text-accent-300 transition-colors">
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-mail-line"></i></span>
              contact@qiuxiamovie.com
            </a>
            <Link to="/about" className="hover:text-accent-300 transition-colors">关于我们</Link>
          </div>
        </div>
      </div>

      {/* main bar */}
      <div
        className={`w-full border-b transition-colors ${
          scrolled ? "bg-background-50/98 backdrop-blur border-background-200" : "bg-background-50 border-transparent"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1280px] items-center gap-4 px-4 md:px-6">
          {/* logo */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-900 text-accent-400 text-xl">
              <i className="ri-film-line"></i>
            </span>
            <span className="font-heading text-xl font-bold text-primary-950 tracking-wide">秋霞电影</span>
          </Link>

          {/* desktop nav */}
          <nav className="hidden md:flex items-center gap-1 ml-2 text-foreground-800">
            <Link to="/" className={`${navLinkBase} ${isActive("/") ? "text-primary-800 font-medium" : ""}`}>
              首页
            </Link>

            <div className="relative" onMouseEnter={() => setOpenMenu("category")} onMouseLeave={() => setOpenMenu(null)}>
              <button
                type="button"
                className={`${navLinkBase} flex items-center gap-1 cursor-pointer ${isActive("/category") ? "text-primary-800 font-medium" : ""}`}
              >
                电影分类
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-down-s-line"></i></span>
              </button>
              {openMenu === "category" && (
                <div className="absolute left-0 top-full w-[420px] rounded-lg border border-background-200 bg-background-50 p-3">
                  <div className="grid grid-cols-3 gap-1">
                    {categoryConfigs.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/category/${c.slug}`}
                        className="rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-primary-100 hover:text-primary-800 transition-colors"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/news" className={`${navLinkBase} ${isActive("/news") ? "text-primary-800 font-medium" : ""}`}>
              影视资讯
            </Link>

            <div className="relative" onMouseEnter={() => setOpenMenu("special")} onMouseLeave={() => setOpenMenu(null)}>
              <button
                type="button"
                className={`${navLinkBase} flex items-center gap-1 cursor-pointer ${isActive("/special") ? "text-primary-800 font-medium" : ""}`}
              >
                专题
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-down-s-line"></i></span>
              </button>
              {openMenu === "special" && (
                <div className="absolute left-0 top-full w-[320px] rounded-lg border border-background-200 bg-background-50 p-3">
                  {specials.map((s) => (
                    <Link
                      key={s.id}
                      to={`/special/${s.id}`}
                      className="block rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-900 transition-colors"
                    >
                      {s.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/about" className={`${navLinkBase} ${isActive("/about") ? "text-primary-800 font-medium" : ""}`}>
              关于我们
            </Link>
          </nav>

          {/* search */}
          <div ref={searchRef} className="relative ml-auto hidden md:block w-[260px]">
            <div className="flex items-center rounded-full border border-background-300 bg-background-100 px-3">
              <span className="w-4 h-4 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line"></i>
              </span>
              <input
                type="text"
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setShowSuggest(true);
                }}
                onFocus={() => setShowSuggest(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitSearch();
                }}
                placeholder="搜索影片 / 资讯"
                className="h-9 w-full bg-transparent px-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
              />
            </div>
            {showSuggest && keyword.trim() && (
              <div className="absolute right-0 top-full mt-2 w-[360px] rounded-lg border border-background-200 bg-background-50 p-2">
                {suggestions.movies.length === 0 && suggestions.news.length === 0 && (
                  <p className="px-3 py-4 text-sm text-foreground-500">没有找到相关内容，换个关键词试试</p>
                )}
                {suggestions.movies.slice(0, 4).map((m) => (
                  <Link
                    key={m.id}
                    to={`/detail/${m.id}`}
                    className="flex items-center gap-3 rounded-md px-3 py-2 hover:bg-primary-100 transition-colors"
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded bg-primary-100 text-primary-700 text-sm">
                      <i className="ri-movie-line"></i>
                    </span>
                    <span className="text-sm text-foreground-800 truncate">{m.title}</span>
                    <span className="ml-auto text-xs text-foreground-500 whitespace-nowrap">{m.rating}分</span>
                  </Link>
                ))}
                {suggestions.news.slice(0, 3).map((n) => (
                  <Link
                    key={n.id}
                    to={`/news/detail/${n.id}`}
                    className="flex items-center gap-3 rounded-md px-3 py-2 hover:bg-accent-100 transition-colors"
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded bg-accent-100 text-accent-700 text-sm">
                      <i className="ri-newspaper-line"></i>
                    </span>
                    <span className="text-sm text-foreground-800 truncate">{n.title}</span>
                  </Link>
                ))}
                {(suggestions.movies.length > 0 || suggestions.news.length > 0) && (
                  <button
                    type="button"
                    onClick={submitSearch}
                    className="mt-1 w-full rounded-md px-3 py-2 text-sm text-primary-700 hover:bg-primary-100 text-left cursor-pointer"
                  >
                    查看全部「{keyword.trim()}」的结果
                  </button>
                )}
              </div>
            )}
          </div>

          {/* mobile toggle */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="ml-auto md:hidden w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-800 cursor-pointer"
            aria-label="打开菜单"
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>

        {/* mobile panel */}
        {mobileOpen && (
          <div className="md:hidden border-t border-background-200 bg-background-50 px-4 pb-5 pt-3">
            <div className="flex items-center rounded-full border border-background-300 bg-background-100 px-3 mb-3">
              <span className="w-4 h-4 flex items-center justify-center text-foreground-500"><i className="ri-search-line"></i></span>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitSearch();
                }}
                placeholder="搜索影片 / 资讯"
                className="h-10 w-full bg-transparent px-2 text-sm outline-none placeholder:text-foreground-500"
              />
            </div>
            <MobileGroup title="电影分类">
              {categoryConfigs.map((c) => (
                <Link key={c.slug} to={`/category/${c.slug}`} className="block py-2 text-sm text-foreground-700">
                  {c.label}电影
                </Link>
              ))}
            </MobileGroup>
            <nav className="mt-2 grid grid-cols-2 gap-2">
              <Link to="/" className="rounded-md bg-background-100 px-3 py-3 text-center text-sm text-foreground-800">首页</Link>
              <Link to="/news" className="rounded-md bg-background-100 px-3 py-3 text-center text-sm text-foreground-800">影视资讯</Link>
              <Link to="/about" className="rounded-md bg-background-100 px-3 py-3 text-center text-sm text-foreground-800">关于我们</Link>
              <Link to={`/special/${specials[0].id}`} className="rounded-md bg-background-100 px-3 py-3 text-center text-sm text-foreground-800">精选专题</Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}

function MobileGroup({ title, children }: { title: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-background-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between py-3 text-sm font-medium text-foreground-900 cursor-pointer"
      >
        {title}
        <span className="w-4 h-4 flex items-center justify-center">
          <i className={open ? "ri-subtract-line" : "ri-add-line"}></i>
        </span>
      </button>
      {open && <div className="pb-2">{children}</div>}
    </div>
  );
}